
import React, { useEffect } from 'react';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SolutionMetadata } from "@/types/solution";
import { createEmptyTemplate } from '@/utils/solutionTemplate';
import PreviewButton from '@/components/PreviewButton';

interface RawContentTabProps {
  jsonContent: string;
  setJsonContent: (content: string) => void;
  solutionName?: string;
  solutionShortDescription?: string;
  solutionCategory?: string;
  solutionTags?: string[];
  imageUrl?: string;
}

const RawContentTab = ({ 
  jsonContent, 
  setJsonContent,
  solutionName,
  solutionShortDescription,
  solutionCategory,
  solutionTags,
  imageUrl
}: RawContentTabProps) => {
  
  // Initialize JSON with metadata if needed
  useEffect(() => {
    
    if (solutionName && (!jsonContent || jsonContent.trim() === '')) {
      const initialDetails = createEmptyTemplate();
      initialDetails.metadata = {
        name: solutionName,
        shortDescription: solutionShortDescription,
        category: solutionCategory,
        tags: solutionTags || [],
        imageUrl: imageUrl
      };

      setJsonContent(JSON.stringify(initialDetails, null, 2));
    } else if (jsonContent && solutionName) {
      // Check if existing JSON needs metadata populated (only for new solutions)
      try {
        const parsedContent = JSON.parse(jsonContent);
        if (!parsedContent.metadata) {
          const metadata: SolutionMetadata = {
            name: solutionName,
            shortDescription: solutionShortDescription,
            category: solutionCategory,
            tags: solutionTags || [],
            imageUrl: imageUrl
          };
          
          const updatedContent = {
            metadata,
            ...parsedContent
          };
          
          setJsonContent(JSON.stringify(updatedContent, null, 2));
        }
      } catch (error) {
        // If JSON is invalid, don't try to parse it
        console.log('JSON parsing error, keeping original content');
      }
    }
  }, [solutionName, solutionShortDescription, solutionCategory, solutionTags, imageUrl, jsonContent, setJsonContent]);

  return (
    <div className="space-y-4 pb-4">
      <div>
        <Label htmlFor="jsonContent" className="text-base">Raw Content (JSON)</Label>
        <Textarea 
          id="jsonContent" 
          value={jsonContent} 
          onChange={(e) => setJsonContent(e.target.value)} 
          placeholder="Edit the raw JSON content of this solution..." 
          className="min-h-[400px] font-mono text-sm whitespace-pre-wrap"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Edit the complete JSON structure above, including metadata, overview, capabilities, and all other solution details. The metadata section at the top contains basic solution info. Make sure to maintain valid JSON format.
        </p>
      </div>
      
      {/* Preview Button */}
      <div className="flex justify-end">
        <PreviewButton
          jsonContent={jsonContent}
          solutionName={solutionName}
          solutionShortDescription={solutionShortDescription}
          solutionCategory={solutionCategory}
          solutionTags={solutionTags}
          imageUrl={imageUrl}
        />
      </div>
    </div>
  );
};

export default RawContentTab;
